import { useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import { messageOffsetAlignmentVerificationAtom } from '../atoms/messages';
import {
  updateVideoAlignmentAtom,
  videosRequiringAlignmentVerificationAtom,
  videosRequiringAlignmentVerificationLoadingAtom,
} from '../atoms/offsetAlignment';
import convertKeys from '../utils/convertKeys';
import useApi, { API_ENDPOINTS } from './api';

export default function useVideosRequiringOffsetAlignmentVerification() {
  const api = useApi();
  const intl = useIntl();

  const setVideosRequiringAlignmentVerification = useSetAtom(
    videosRequiringAlignmentVerificationAtom,
  );
  const setVideosRequiringAlignmentVerificationLoading = useSetAtom(
    videosRequiringAlignmentVerificationLoadingAtom,
  );
  const updateVideoAlignment = useSetAtom(updateVideoAlignmentAtom);
  const setMessage = useSetAtom(messageOffsetAlignmentVerificationAtom);

  const FAILURE_MESSAGE = {
    text: intl.formatMessage({
      defaultMessage: 'Failed to update alignment status',
      id: 'd34ed',
    }),
    type: 'error',
  };
  const SUCCESS_MESSAGE_ALIGNED = intl.formatMessage({
    defaultMessage: 'Video Made Available In Hudl Statsbomb',
    id: '539b6',
  });
  const SUCCESS_MESSAGE_MISALIGNED = intl.formatMessage({
    defaultMessage:
      'Video Will Be Unavailable In Hudl Statsbomb Until Alignment Issue Has Been Resolved',
    id: '1dc3e',
  });

  const clear = () => {
    setVideosRequiringAlignmentVerification([]);
  };

  const requestVideosRequiringAlignmentVerification = async (
    competitionId,
    seasonId,
  ) => {
    setVideosRequiringAlignmentVerificationLoading(true);
    try {
      const url =
        competitionId && seasonId
          ? `${API_ENDPOINTS.videoService}/${competitionId}/${seasonId}/videos-requiring-offset-validation`
          : `${API_ENDPOINTS.videoService}/streams/requiring-offset-validation`;
      setMessage(null);
      const response = await api.get(url);
      setVideosRequiringAlignmentVerification(
        convertKeys(response.data?.events || []),
      );
      setVideosRequiringAlignmentVerificationLoading(false);
    } catch (err) {
      setVideosRequiringAlignmentVerificationLoading(false);
      setMessage({
        text: intl.formatMessage({
          defaultMessage:
            'Failed to get videos requiring offset alignment verification',
          id: 'c4b95',
        }),
        type: 'error',
      });
    }
  };

  const updateAlignmentStatus = async ({ id, videoReference, newStatus }) => {
    try {
      const { matchId, period, track, version } = videoReference;
      const url = `${
        API_ENDPOINTS.videoService
      }/${matchId}/${period}/${track}/${version}/offset-validation?video_is_aligned=${newStatus.toString()}`;

      const { status } = await api.put(url);

      if (status === 200) {
        updateVideoAlignment({
          id,
          isAligned: newStatus,
        });
        setMessage({
          text: newStatus
            ? SUCCESS_MESSAGE_ALIGNED
            : SUCCESS_MESSAGE_MISALIGNED,
          type: newStatus ? 'success' : 'info',
        });
      } else {
        setMessage(FAILURE_MESSAGE);
      }
    } catch {
      setMessage(FAILURE_MESSAGE);
    }
  };

  return {
    clearVideosRequiringOffsetVerification: clear,
    requestVideosRequiringAlignmentVerification,
    updateAlignmentStatus,
  };
}
